import { create } from 'zustand'
import { SidebarType } from '../../components/Sidebar/Sidebar.tsx'
import type { FolderContent } from '@/features/media-file/domain/models/media-folder.ts'
import { type Clip, isVoiceClip } from '@/features/clips/domain/models/clip.ts'
import type { Voice } from '@/features/voice/domain/models/voice.ts'
import useSpeechDialogStore from '@/components/SpeechDialog/speech-dialog.store.ts'
import { speechSelector, useSpeechStore } from '@/shared/store/speech.store'
import { scriptSelector, useScriptStore } from '@/shared/store/script.store'
import { fetchFileInfo } from '@/features/media-file/domain/use-cases/fetch-file-info'
import type { UUID } from '@/features/shared/data/string.types.ts'

type UiStore = {
  isSidebarOpen: boolean
  setIsSidebarOpen: (isOpen: boolean) => void
  sidebarType?: SidebarType
  setSidebarType: (type: SidebarType) => void
  selectedClip: Clip | undefined
  setSelectedClip: (clip: Clip | undefined) => void
  pixelsPerSecond: number
  zoom: number
  setZoom: (zoomLevel: number) => void
  mediaFilesCurrentFolder: FolderContent | undefined
  setMediaFilesCurrentFolder: (content: FolderContent) => void
  onVoiceSelected: ((voice: Voice) => void) | undefined
  openVoiceSelector: (onSelect: (voice: Voice) => void) => void
  selectVoice: (voice: Voice) => void
  clearVoiceCallback: () => void
  isMagnetEnabled: boolean
  setMagnetEnabled: (isEnabled: boolean) => void
  isSpeechDialogOpen: boolean
  setSpeechDialogOpen: (isOpen: boolean) => void
  openSpeechDialogForClip: (clip: Clip) => void
  openSpeechDialogForNew: () => void
}

const DEFAULT_PX_PER_SECOND = 40

const useUiStore = create<UiStore>((set, get) => ({
  isSidebarOpen: false,
  setIsSidebarOpen: (isOpen: boolean) => set({ isSidebarOpen: isOpen }),
  setSidebarType: (type: SidebarType) =>
    set({ sidebarType: type, isSidebarOpen: true }),
  selectedClip: undefined,
  setSelectedClip: (clip: Clip | undefined) => {
    set({ selectedClip: clip })
  },
  pixelsPerSecond: DEFAULT_PX_PER_SECOND,
  zoom: 1,
  setZoom: (zoom: number) =>
    set({ zoom: zoom, pixelsPerSecond: DEFAULT_PX_PER_SECOND * zoom }),
  mediaFilesCurrentFolder: undefined,
  setMediaFilesCurrentFolder: (content: FolderContent) =>
    set({ mediaFilesCurrentFolder: content }),
  onVoiceSelected: undefined,
  openVoiceSelector: (onSelect: (voice: Voice) => void) =>
    set({
      onVoiceSelected: onSelect,
      sidebarType: SidebarType.Voices,
      isSidebarOpen: true,
    }),
  selectVoice: (voice: Voice) => {
    const { onVoiceSelected } = get()
    if (onVoiceSelected) onVoiceSelected(voice)
    set({ onVoiceSelected: undefined })
  },
  clearVoiceCallback: () => set({ onVoiceSelected: undefined }),
  isMagnetEnabled: true,
  setMagnetEnabled: (isEnabled: boolean) => set({ isMagnetEnabled: isEnabled }),
  isSpeechDialogOpen: false,
  setSpeechDialogOpen: (isOpen: boolean) => {
    const { selectedClip } = get()
    set({
      isSpeechDialogOpen: isOpen,
      selectedClip: isOpen ? selectedClip : undefined,
    })
  },
  openSpeechDialogForClip: (clip: Clip) => {
    if (!isVoiceClip(clip)) return

    const speechDialogStore = useSpeechDialogStore.getState()
    const speechStore = useSpeechStore.getState()
    const scriptStore = useScriptStore.getState()

    // Set the clip being edited
    speechDialogStore.setEditingClip(clip)

    // Get speech data from the clip
    const speech = speechSelector(clip.mediaId)(speechStore)

    if (speech) {
      const script = scriptSelector(speech.scriptId)(scriptStore)

      if (speech.type === 'sts') {
        speechDialogStore.selectScript() // Clear any existing script
        speechDialogStore.setSelectedTab('speech')
        speechDialogStore.setScriptText('')
        speechDialogStore.selectVoice(speech.voice)
        speechDialogStore.setExpressive(speech.expressive ?? false)
        speechDialogStore.setSpeed(speech.speed)

        if (speech.fileId) {
          const loadStsFile = async () => {
            try {
              speechDialogStore.setSelectedAudioFileId(speech.fileId)
              if (speech.fileId) {
                const mediaFile = await fetchFileInfo(speech.fileId)
                speechDialogStore.setAudioFiles([
                  { id: speech.fileId as UUID, kind: 'media', mediaFile },
                ])
              }
            } catch (error) {
              console.error('Failed to load STS audio file:', error)
            }
          }
          void loadStsFile()
        }
      } else if (script) {
        speechDialogStore.selectScript(script.id)
        speechDialogStore.setSelectedTab('text')
        speechDialogStore.setScriptText(script.text)
        speechDialogStore.selectVoice(speech.voice)
        speechDialogStore.setExpressive(speech.expressive ?? false)
        speechDialogStore.setSpeed(speech.speed)
      } else {
        speechDialogStore.reset()
        speechDialogStore.setSelectedTab('text')
      }
    } else {
      speechDialogStore.reset()
      speechDialogStore.setSelectedTab('text')
    }

    set({ isSpeechDialogOpen: true })
  },
  openSpeechDialogForNew: () => {
    const speechDialogStore = useSpeechDialogStore.getState()

    // Reset the dialog for creating a new clip
    speechDialogStore.reset()
    speechDialogStore.setEditingClip(undefined)
    speechDialogStore.setSelectedTab('text') // Default to text tab for new clips

    set({ isSpeechDialogOpen: true })
  },
}))

export default useUiStore
